import {Point} from '@app/modules/svg/model/Point';
import {Path} from '@app/modules/svg/model/path/Path';
import {MoveTo} from '@app/modules/svg/model/path/point/MoveTo';
import {LineTo} from '@app/modules/svg/model/path/line/LineTo';

export interface Fill {
  matrix: boolean[][];
  touchesTheBorder: boolean;
}

export interface Color {
  r: number;
  g: number;
  b: number;
  a: number;
}

export namespace Color {
  export function equals(color1: Color, color2: Color): boolean {
    return color1.r === color2.r && color1.g === color2.g && color1.b === color2.b && color1.a === color2.a;
  }
  export function fromHex(hex: string): Color {
    if (hex === 'none') {
      return {
        r: 0,
        g: 0,
        b: 0,
        a: 0,
      };
    }

    hex = hex.slice(1);

    if (hex.length === 6) {
      hex += 'FF';
    }

    const array: string[] = hex.match(/.{1,2}/g);

    return {
      r: parseInt(array[0], 16),
      g: parseInt(array[1], 16),
      b: parseInt(array[2], 16),
      a: parseInt(array[3], 16),
    };
  }
}

export class ImageProcessor {
  private readonly buffer: Uint8Array;
  private readonly _imageWidth: number;
  private readonly _imageHeight: number;

  public constructor(imageData: ImageData) {
    this.buffer = new Uint8Array(imageData.data.buffer);
    this._imageWidth = imageData.width;
    this._imageHeight = imageData.height;
  }

  public get width(): number {
    return this._imageWidth;
  }
  public get height(): number {
    return this._imageHeight;
  }

  public getPixel(x: number, y: number): Color {
    const indices: number[] = ImageProcessor.getColorIndicesForCoordinate(x, y, this._imageWidth);

    return {
      r: this.buffer[indices[0]],
      g: this.buffer[indices[1]],
      b: this.buffer[indices[2]],
      a: this.buffer[indices[3]],
    };
  }

  public setPixel(x: number, y: number, color: Color): void {
    const indices: number[] = ImageProcessor.getColorIndicesForCoordinate(x, y, this._imageWidth);

    this.buffer[indices[0]] = color.r;
    this.buffer[indices[1]] = color.g;
    this.buffer[indices[2]] = color.b;
    this.buffer[indices[3]] = color.a;
  }

  public fill(point: Point, fillColor: Color): Fill {
    const targetColor: Color = structuredClone(this.getPixel(point.x, point.y));

    if (Color.equals(targetColor, fillColor)) {
      return { matrix: [], touchesTheBorder: false };
    }

    const fill = {
      matrix: Array.from({ length: this._imageHeight }, () => Array(this._imageWidth).fill(false)),
      touchesTheBorder: false,
    };

    const queue: Point[] = [point];
    const directions: number[][] = [[-1, 0], [1, 0], [0, -1], [0, 1]];

    while (queue.length > 0) {
      const {x, y} = queue.shift();

      if (Color.equals(this.getPixel(x, y), fillColor)) {
        continue;
      }

      this.setPixel(x, y, fillColor);
      fill.matrix[y][x] = true;

      for (const [dx, dy] of directions) {
        const newX = x + dx;
        const newY = y + dy;
        if (!fill.touchesTheBorder && (newX <= 0 || newX >= this._imageWidth || newY <= 0 || newY >= this._imageHeight)) {
          fill.touchesTheBorder = true;
        }

        if (newX >= 0 && newX < this._imageWidth && newY >= 0 && newY < this._imageHeight && Color.equals(this.getPixel(newX, newY), targetColor)) {
          queue.push({x: newX, y: newY});
        }
      }
    }

    return fill;
  }

  /**
   *   0123456789...
   * 0 11111111110001110000000
   * 1 11111111100000011100000
   * 2 11111111000000001111000
   * 3 11111111100000011100000
   * 4 11111111111111000000000
   * 5 00111111110000000000000
   * 6 00001111110000000000000
   *
   * @returns
   * [
   *   { p0: { x: 0, y: 0 }, p1: { x: 9, y: 0 } }, { p0: { x: 13, y: 0 }, p1: { x: 16, y: 0 } },
   *   { p0: { x: 0, y: 1 }, p1: { x: 8, y: 1 } }, { p0: { x: 16, y: 1 }, p1: { x: 19, y: 1 } },
   *   ...
   * ]
   * */
  public static matrixToFillLines(matrix: boolean[][]): Path {
    const path: Path = new Path();

    for (let y = 0; y < matrix.length; y++) {
      const linePath: Path = ImageProcessor.detectLines(matrix[y], y);

      if (linePath.commands.length > 0) {
        path.addPath(linePath);
      }
    }

    return path;
  }

  /**
   * 0123456789...
   * 11111111110001110000000
   * */
  private static detectLines(row: boolean[], y: number): Path {
    const path: Path = new Path();
    let lineStart: number | null = null;

    for (let x = 0; x < row.length; x++) {
      /* start of line */
      if (lineStart == null && row[x]) {
        lineStart = x;
      } else if ((!row[x] || x === row.length - 1) && lineStart !== null) { /* end of line */
        path.add(new MoveTo({ x: lineStart, y }));
        path.add(new LineTo({ x, y }));

        lineStart = null;
      }
    }

    return path;
  }

  private static getColorIndicesForCoordinate(x: number, y: number, width: number): number[] {
    const red = y * (width * 4) + x * 4;
    return [red, red + 1, red + 2, red + 3];
  }
}
